import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { UI, btnSx } from "../components/ActionDrawer";
import FileExpenseClaimModal from "../components/FileExpenseClaimModal";
import { CardGrid, Empty, GridCard, PageHead, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const rupee = (paise) =>
  paise === null || paise === undefined
    ? "—"
    : `₹${(Number(paise) / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const STATUS_TAG = {
  draft: { tone: "i", label: "Draft" },
  submitted: { tone: "s", label: "Submitted" },
  approved: { tone: "bl", label: "Approved" },
  rejected: { tone: "b", label: "Rejected" },
  queried: { tone: "s", label: "Queried" },
  paid: { tone: "g", label: "Paid" },
};

const PAGE_SIZE = 15;

/* Flow 5 — every claim this technician has filed, plus a monthly total. No
   "has my claim been paid" question needed once this exists. */
const MyExpenses = () => {
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [rows, setRows] = useState([]);
  const [monthlyTotals, setMonthlyTotals] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [fileExpenseOpen, setFileExpenseOpen] = useState(false);

  const fetchRows = async (targetPage = page) => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchMyExpenses(token, { page: targetPage, limit: PAGE_SIZE });
      setRows(res.data || []);
      setMonthlyTotals(res.monthlyTotals || null);
      setPagination(res.pagination || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching your expense claims");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const kpis = monthlyTotals
    ? [
        { label: "Claims this month", value: monthlyTotals.totalClaims },
        { label: "Total this month", value: rupee(monthlyTotals.totalAmountPaise) },
        { label: "Awaiting decision", value: monthlyTotals.byStatus?.submitted?.count || 0 },
        { label: "Paid", value: monthlyTotals.byStatus?.paid?.count || 0 },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="My Expenses"
        lede="Every claim you've filed and its status — including whether it's been paid."
        kpis={kpis}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "14px" }}>
        <Box
          component="button"
          type="button"
          onClick={() => setFileExpenseOpen(true)}
          sx={btnSx("solid")}
        >
          + New claim
        </Box>
      </Box>

      {loadFailed ? (
        <Empty
          title="Could not load your claims"
          sub={
            <Box
              component="button"
              type="button"
              onClick={fetchRows}
              sx={{ ...btnSx("ghost"), margin: "12px auto 0", display: "block" }}
            >
              Retry
            </Box>
          }
        />
      ) : rows.length === 0 && !isLoading ? (
        <Empty title="No claims yet" sub="File one from a job, or with the button above." />
      ) : (
        <CardGrid>
          {rows.map((row) => {
            const tag = STATUS_TAG[row.status] || STATUS_TAG.draft;
            const dateLine =
              row.dateFrom === row.dateTo
                ? shortDate(row.dateFrom)
                : `${shortDate(row.dateFrom)} – ${shortDate(row.dateTo)}`;
            return (
              <GridCard
                key={row.id}
                title={`${row.itemCount} item${row.itemCount === 1 ? "" : "s"}`}
                tag={<Tag tone={tag.tone}>{tag.label}</Tag>}
                lines={
                  <>
                    {rupee(row.totalAmountPaise)} · {dateLine}
                    <br />
                    {row.clinicName || "Not linked to an order"}
                    {row.status === "paid" && row.paymentReference ? (
                      <>
                        <br />
                        Ref: {row.paymentReference}
                      </>
                    ) : null}
                    {row.status === "rejected" && row.rejectionReason ? (
                      <>
                        <br />
                        {row.rejectionReason}
                      </>
                    ) : null}
                    {row.status === "queried" && row.queryNote ? (
                      <>
                        <br />
                        {row.queryNote}
                      </>
                    ) : null}
                    {row.anyPolicyFlag ? (
                      <>
                        <br />
                        <Box component="span" sx={{ color: UI.red }}>
                          One or more items are over the daily food limit
                        </Box>
                      </>
                    ) : null}
                  </>
                }
              />
            );
          })}
        </CardGrid>
      )}

      {pagination && pagination.totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: "8px", marginBottom: "10px" }}>
          <Box
            component="button"
            type="button"
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            disabled={pagination.page <= 1}
            sx={btnSx("ghost", pagination.page <= 1)}
          >
            Previous
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => setPage((p) => Math.min(p + 1, pagination.totalPages))}
            disabled={pagination.page >= pagination.totalPages}
            sx={btnSx("ghost", pagination.page >= pagination.totalPages)}
          >
            Next
          </Box>
        </Box>
      )}

      <FileExpenseClaimModal
        open={fileExpenseOpen}
        orderId={null}
        onClose={() => setFileExpenseOpen(false)}
        onSaved={() => {
          setFileExpenseOpen(false);
          fetchRows();
        }}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default MyExpenses;
